import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  PLATFORM_ID,
  QueryList,
  Renderer2,
  SimpleChanges,
  ViewChild,
  ViewChildren,
  ViewEncapsulation
} from '@angular/core';
import { v4 as uuid } from 'uuid';
import { Subscription } from 'rxjs';
import { Router } from '@angular/router';
import {
  BrandsEnum,
  DeviceService,
  IconSizeEnum,
  IRating, SdButtonIconComponent, SdFormLibelleComponent,
  SdLibConfig,
  SizesEnum
} from '@seb/web-design-system/core';
import { isPlatformBrowser, NgTemplateOutlet, NgClass } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'sd-rating',
  templateUrl: './rating.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./rating.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [
    NgTemplateOutlet,
    SdFormLibelleComponent,
    SdButtonIconComponent,
    NgClass,
    TranslateModule
  ]
})
export class SdRatingComponent
implements OnInit, OnDestroy, OnChanges, AfterViewInit
{
  constructor(
    private deviceService: DeviceService,
    protected cdRef: ChangeDetectorRef,
    private router: Router,
    private renderer: Renderer2,
    @Inject(PLATFORM_ID) private readonly platformId: any,
    private readonly sdLibConfig: SdLibConfig,
  ) {}

  urlIcons = '/assets/icons/';
  iconSize = IconSizeEnum;
  uuidRating: string = uuid();
  starQty;
  mobileSubscription: Subscription;
  isMobile$: boolean;
  starsTable = [];
  iconSizeStars = 20;
  iconStarSpacing = 2;
  uuidMask: string = uuid();
  configBrand: BrandsEnum = BrandsEnum.moulinex;

  @Input()
  ratingSize: SizesEnum = SizesEnum.s;

  @Input()
  brand: BrandsEnum;

  @Input()
  indexFollow = true;

  @Input()
  labelHidden = true;

  @Input()
  labelTitle: string;

  @Input()
  readonly = false;

  @Input()
  required = false;

  @HostBinding('class.is-scale')
  @Input()
  isScale = false;

  @HostBinding('class.is-large')
  @Input()
  isLarge = false;

  @Input()
  ratings: IRating;

  @ViewChild('progressStars', { read: ElementRef })
  progressStars: ElementRef;

  @ViewChild('defaultStars', { read: ElementRef })
  defaultStars: ElementRef;

  @ViewChildren('rateInput')
  inputs;

  @ViewChildren('rateButton', { read: ElementRef })
  buttons: QueryList<ElementRef>;

  @Input()
  maxStar = 5;

  @Input()
  showAverageRatingsLabel = false;

  @Input()
  showNumberOfReviews = true;

  @Input()
  wrapReviewsOnMobile = false;

  @Input()
  reviewsClickable = false;

  @Input()
  hideRatingStars: string;

  @Input()
  hideRatingLabel: string;

  @Input()
  hideRatingNumber: string;

  @Output()
  ratingsChanged = new EventEmitter<number>();

  @Output()
  reviewsClicked = new EventEmitter<number>();

  @Output()
  reviewsSeeAllClicked = new EventEmitter<void>();

  @HostBinding('class.is-one-line')
  get isOneLine(): boolean {
    return !(this.wrapReviewsOnMobile && this.isMobile$) && !this.isLarge;
  }

  @HostBinding('class.has-no-rating-label')
  get hasRatingLabel(): boolean {
    return !this.showAverageRatingsLabel;
  }

  ngOnInit() {
    this.mobileSubscription = this.deviceService.isMobile.subscribe((x) => {
      this.isMobile$ = x;
      this.cdRef.markForCheck();
    });

    this.manageRatingStarSizes();

    if (!this.readonly && this.ratings.url) {
      this.readonly = true;
    }

    this.showAverageRatingsLabel = this.isLarge
      ? true
      : this.showAverageRatingsLabel;
    this.readonly = this.isLarge ? true : this.readonly;

    this.starQty = Array(this.maxStar * 2)
      .fill('')
      .map((x, i) => {
        return {
          value: (i + 1) / 2
        };
      });

    this.configBrand = this.sdLibConfig.currentBrand;

    for (let i = 0; i < this.maxStar; i++) {
      this.starsTable.push(i);
    }
    this.cdRef.markForCheck();
  }

  ngAfterViewInit() {
    this.setProgressStars();
  }

  ngOnDestroy() {
    this.mobileSubscription?.unsubscribe();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['averageRatings']) {
      this.updateCheckedStar(this.ratings?.averageRatings * 2 - 1);
    }

    if (changes.maxStar) {
      this.starQty = Array(this.maxStar * 2)
        .fill('')
        .map((x, i) => {
          return {
            value: (i + 1) / 2
          };
        });
    }

    this.setProgressStars();
    this.cdRef.markForCheck();
  }

  manageRatingStarSizes() {
    switch (this.ratingSize) {
      case SizesEnum.s: {
        this.iconSizeStars = 20;
        this.iconStarSpacing = 2;
        break;
      }
      case SizesEnum.xs: {
        this.iconSizeStars = 12;
        this.iconStarSpacing = 1;
        break;
      }
    }
    this.cdRef.markForCheck();
  }

  rateIndex(index: number): number {
    return (index + 1) / 2;
  }

  selectRatings(index: number) {
    if (!this.readonly && !this.ratings?.url) {
      this.updateCheckedStar(index);
      this.ratingsChanged.emit(this.rateIndex(index));
    }
  }

  setProgressStars() {
    if (isPlatformBrowser(this.platformId)) {
      if (this.readonly) {
        const progressBarElement = this.progressStars?.nativeElement;
        const progressBarStarsContainerElement =
          progressBarElement?.querySelector('.c__svg-default-star-container');
        const progressBarContainerElement = progressBarElement?.querySelector(
          '.c__progress-bar-container',
        );
        const progressBarStarsBar =
          this.progressStars?.nativeElement?.querySelector('rect');
        if (progressBarStarsBar) {
          this.renderer?.setStyle(
            progressBarStarsBar,
            'mask',
            'url("#svg-mask-stars' + this.uuidMask + '")',
          );
          const percentRating =
            (this.ratings.averageRatings * 100) / this.maxStar;
          this.renderer?.setStyle(
            progressBarStarsBar,
            'width',
            percentRating + '%',
          );
          this.renderer?.setStyle(
            progressBarElement,
            'height',
            this.iconSizeStars / 10 + 'rem',
          );
          this.renderer?.setStyle(
            progressBarStarsContainerElement,
            'height',
            this.iconSizeStars / 10 + 'rem',
          );
          this.renderer?.setStyle(
            progressBarStarsContainerElement,
            'line-height',
            this.iconSizeStars / 10 + 'rem',
          );
          this.renderer?.setStyle(
            progressBarContainerElement,
            'height',
            this.iconSizeStars / 10 + 'rem',
          );
        }
      }
    }
  }

  /**
   * Update the template to select the right 'star'.
   */
  updateCheckedStar(index: number) {
    if (this.buttons) {
      const activeButton = this.buttons.find((button) => {
        return button.nativeElement.getAttribute('aria-pressed') === 'true';
      });

      if (activeButton) {
        activeButton.nativeElement.setAttribute('aria-pressed', 'false');
      }

      if (this.buttons.toArray()[index]) {
        this.buttons.toArray()[index].nativeElement.setAttribute('aria-pressed', 'true');
        if (this.ratings) {
          this.ratings.averageRatings = this.rateIndex(index);
        }
      }
    }
  }

  onReviewsSeeAllClicked(url?: string) {
    this.reviewsSeeAllClicked.emit();
    if (url) {
      this.router.navigateByUrl(url);
    }
  }
}
